import React, { useEffect, useState } from 'react';
import {
    VStack,
    HStack,
    Image,
    FlatList,
    Pressable,
    ChevronLeftIcon,
    Box,
    ScrollView,
    View
} from 'native-base';
import { Caption, H1 } from '~/shared/Typography';
import {
    useAddItemToCartMutation,
    useCreateCheckoutMutation,
    useDutchieStore,
    useTheme,
} from '@springbig/shared';
import { Section } from '~/shared/Theme';
import { SBDropdown } from '~/shared/SBDropdown';
import { QuantityPicker } from '~/shared/QuantityPicker';
import { ProductImage, ProductVariant } from '@springbig/shared/src/services/dutchie/schema-types';
import { VariantCard } from '~/pages/shop/components/VariantCard';
import { useAppNavigate } from '~/pages';
import { Cannabinoid } from '~/pages/shop/components/Cannabinoid';
import { useToast } from '~/hooks/toast';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { Button } from '~/shared/Theme';

export const ProductDetailsPage = () => {

    const {
        button_text,
        button_primary,
        radius_md,
        text
    } = useTheme();

    const { navigate, params } = useAppNavigate();
    const { errorToast } = useToast();

    const {
        menuType,
        product,
        checkout,
        fullfillmentOption,
        retailer,
        setCheckout,
        cartItemsAmount,
    } = useDutchieStore();

    const [selectedVariant, setSelectedVariant] =
        useState<ProductVariant>(product?.variants[0]);
    const [selectedQuantity, setSelectedQuantity] =
        useState<number>(1);

    const {
        mutate: addItem,
        data: newAddItem,
        isLoading: addItemLoading,
    } = useAddItemToCartMutation();

    const {
        mutate: createCheckout,
        data: newCheckout,
        isLoading: checkoutLoading,
    } = useCreateCheckoutMutation();

    useEffect(() => {

        if (newCheckout) {
            setCheckout(newCheckout.createCheckout);
            addItem({
                retailerId: retailer.id,
                checkoutId: newCheckout.createCheckout.id,
                quantity: selectedQuantity,
                option: selectedVariant.option,
                productId: product.id,
            }, {
                onError: (error) => {

                    errorToast(error.data.errors[0].message);
                }
            });
        }
    }, [newCheckout]);

    useEffect(() => {

        if (newAddItem) {
            setCheckout(newAddItem.addItem);
        }
    }, [newAddItem]);

    const isDiscounted = (): boolean => {

        let state = false;
        if (menuType === 'RECREATIONAL') {
            state = !!selectedVariant.specialPriceRec;
        }

        if (menuType === 'MEDICAL') {
            state = !!selectedVariant.specialPriceMed;
        }

        return state;
    };

    const displayFinalPrice = (variant: ProductVariant): string => {

        let finalPrice;
        if (menuType === 'RECREATIONAL') {
            finalPrice = variant.specialPriceRec ? variant.specialPriceRec : variant.priceRec;
        }

        if (menuType === 'MEDICAL') {
            finalPrice = variant.specialPriceMed ? variant.specialPriceMed : variant.priceMed;
        }

        return `$${(finalPrice * selectedQuantity).toFixed(2)}`;
    };

    const displayRawPrice = (variant: ProductVariant): string => {

        let finalPrice;
        if (menuType === 'RECREATIONAL') {
            finalPrice = variant.priceRec;
        }

        if (menuType === 'MEDICAL') {
            finalPrice = variant.priceMed;
        }

        return `$${(finalPrice * selectedQuantity).toFixed(2)}`;
    };

    const htmlToPlainText = (input: string): string => {
        if (!input) {
            return "";
        }

        let s = input;

        s = s.replace(/<\s+([a-zA-Z])/g, "<$1");

        s = s.replace(/<li[^>]*>/gi, "\n• ");
        s = s.replace(/<\/li>/gi, "");

        s = s.replace(/<(\/)?(p|div|h[1-6]|section|article|br|tr|table|thead|tbody|tfoot)[^>]*>/gi, (m, slash, tag) => {
            const normalized = tag?.toLowerCase();
            if (normalized === "br" || normalized?.startsWith("h") || normalized === "tr") {
                return "\n\n";
            }

            return "\n\n";
        });

        s = s.replace(/<\/?[^>]+(>|$)/g, "");

        s = s.replace(/&#(\d+);/g, (_, dec) => {
            const code = Number(dec);
            try { return String.fromCodePoint(code); }
            catch { return ""; }
        });

        s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
            const code = parseInt(hex, 16);
            try { return String.fromCodePoint(code); }
            catch { return ""; }
        });

        const namedEntities: Record<string,string> = {
            "nbsp": " ",
            "amp": "&",
            "lt": "<",
            "gt": ">",
            "quot": "\"",
            "apos": "'",
            "rsquo": "’",
            "lsquo": "‘",
            "ldquo": "“",
            "rdquo": "”",
            "mdash": "—",
            "ndash": "–",
        };
        s = s.replace(/&([a-zA-Z]+);/g, (m, name) => {
            return namedEntities[name] ?? m;
        });

        s = s.split("\n").map(line => line.replace(/\s+/g, " ").trim()).join("\n");
        s = s.replace(/\n{3,}/g, "\n\n");
        s = s.trim();

        return s;
    };

    return ( product ?
        <Section h={'100%'} w={'clamp(320px, 100vw, 480px)'} overflowX={'hidden'}>
            <VStack justifyContent={'space-between'} h={'100%'}>
                <HStack alignItems={'center'} justifyContent={'space-between'} space={4} p={4}>
                    <Pressable
                        onPress={() => navigate(`w/${params.wallet_id}/shop/`)}
                    >
                        <ChevronLeftIcon size={'lg'} color={button_primary} />
                    </Pressable>
                    <VStack flex={1}>
                        <H1 fontSize={14} textAlign={'center'}>
                            { product.name }
                        </H1>
                        <Caption textAlign={'center'}>
                            { product.brand.name }
                        </Caption>
                    </VStack>
                    <Button
                        h={10}
                        w={10}
                        rounded={'full'}
                        modifier={'small'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        onPress={() => {

                            navigate(`w/${params.wallet_id}/shop/cart`);
                        }}
                    >
                        <View>
                            <SpIcon color={button_text} icon={faShoppingCart} />
                            { cartItemsAmount > 0 &&
                                <Box
                                    position={'absolute'}
                                    top={-15}
                                    left={-15}
                                    bg={'red'}
                                    color={'white'}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    rounded={'full'}
                                    h={4}
                                    w={4}
                                >
                                    <Caption fontSize={10}>
                                        { cartItemsAmount }
                                    </Caption>
                                </Box>
                            }
                        </View>
                    </Button>
                </HStack>
                <ScrollView>
                    <VStack
                        space={4}
                        px={2}
                    >
                        { product.images.length  === 1 ?
                            <Image
                                src={product.images[0].url + '?w=450&h=450'}
                                rounded={radius_md}
                                w={'clamp(320px, 93vw, 450px)'}
                                h={'clamp(320px, 93vw, 450px)'}
                            />
                            :
                            <FlatList
                                py={2}
                                horizontal={true}
                                data={product.images}
                                renderItem={( { item, index }: { item: ProductImage, index: number}) =>

                                    <Image
                                        key={index}
                                        src={item.url + '?w=450&h=450'}
                                        rounded={radius_md}
                                        mr={product.images.length - 1 !== index ? 4 : 0}
                                        w={'450px'}
                                        h={'450px'}
                                    />
                                }
                            />
                        }
                        <Caption textAlign={'center'}>
                            { htmlToPlainText(product.description) }
                        </Caption>
                    </VStack>

                    <VStack space={4} px={2} py={4}>
                        { product.effects && product.effects.length > 0 &&
                            <SBDropdown
                                title={'effects'}>
                                <HStack
                                    flexWrap={'wrap'}
                                    justifyContent={'space-around'}
                                    space={4}
                                >
                                    { product.effects.map((effect, index) =>

                                        <Caption key={index}>
                                            { effect.toLowerCase().replace('_', ' ') }
                                        </Caption>
                                    )}
                                </HStack>
                            </SBDropdown>
                        }
                        { product.cannabinoids?.length > 0 &&
                            <SBDropdown title={'cannabinoids'}>
                                <VStack
                                    space={2}
                                    p={2}
                                >
                                    { product.cannabinoids.map((type, index) =>

                                        <Cannabinoid type={type} key={index} />
                                    )}
                                </VStack>
                            </SBDropdown>
                        }
                    </VStack>

                    { product.variants.length > 1 &&
                        <Box px={2}>
                            <FlatList
                                horizontal={true}
                                data={product.variants}
                                keyExtractor={(variant: ProductVariant, index: number) => `${variant.id}-${index}`}
                                renderItem={({ item, index }: { item: ProductVariant; index: number }) =>

                                    <>
                                        { menuType === 'RECREATIONAL' ?
                                            <VariantCard
                                                mr={2}
                                                key={index}
                                                id={item.id}
                                                price={item.priceRec}
                                                specialPrice={item.specialPriceRec}
                                                name={item.option}
                                                isSelected={item.id === selectedVariant.id}
                                                onPress={() => setSelectedVariant(item)}
                                            />
                                            :
                                            <VariantCard
                                                mr={2}
                                                key={index}
                                                id={item.id}
                                                price={item.priceMed}
                                                specialPrice={item.specialPriceMed}
                                                name={item.option}
                                                isSelected={item.id === selectedVariant.id}
                                                onPress={() => setSelectedVariant(item)}
                                            />
                                        }
                                    </>
                                }
                            />
                        </Box>
                    }

                    <VStack space={4} px={2} py={4} justifyContent={'center'}>
                        { selectedVariant.quantity > 1 &&
                            <QuantityPicker
                                isNullable={false}
                                quantity={selectedQuantity}
                                maxQuantity={selectedVariant.quantity}
                                setQuantity={(quantity) => setSelectedQuantity(quantity)}
                                isLoading={false}
                                variant={'large'}
                            />
                        }

                        <Pressable
                            position={'relative'}
                            disabled={addItemLoading || checkoutLoading}
                            opacity={addItemLoading || checkoutLoading ? .5 : 1}
                            onPress={() => {

                                if (checkout) {
                                    addItem({
                                        retailerId: retailer.id,
                                        checkoutId: checkout.id,
                                        quantity: selectedQuantity,
                                        option: selectedVariant.option,
                                        productId: product.id,
                                    }, {
                                        onError: (error) => {

                                            errorToast(error.data.errors[0].message);
                                        }
                                    });
                                }
                                else {
                                    createCheckout({
                                        pricingType: menuType,
                                        orderType: fullfillmentOption.key,
                                        retailerId: retailer.id,
                                    });
                                }
                            }}
                        >
                            <HStack>
                                <VStack px={4} py={2} alignItems={'center'} justifyContent={'center'}>
                                    <Box
                                        position={'absolute'}
                                        borderColor={button_primary}
                                        bg={button_text}
                                        borderWidth={1}
                                        borderRightWidth={0}
                                        roundedLeft={radius_md}
                                        h={16}
                                        w={'150%'}
                                        left={0}
                                        top={0}
                                        zIndex={-1}
                                    ></Box>
                                    {/*{ isDiscounted() &&*/}
                                    {/*    <Caption*/}
                                    {/*        textAlign={'center'}*/}
                                    {/*        fontSize={14}*/}
                                    {/*        color={text}*/}
                                    {/*        opacity={.4}*/}
                                    {/*        style={{ textDecorationLine: 'line-through' }}*/}
                                    {/*    >*/}
                                    {/*        { displayRawPrice(selectedVariant) }*/}
                                    {/*    </Caption>*/}
                                    {/*}*/}
                                    <Caption
                                        textAlign={'center'}
                                        fontSize={16}
                                        color={text}
                                    >
                                        { displayFinalPrice(selectedVariant) }
                                    </Caption>
                                </VStack>
                                <HStack
                                    bg={button_primary}
                                    rounded={radius_md}
                                    py={4}
                                    px={8}
                                    h={16}
                                    flex={1}
                                    alignItems={'center'}
                                    isDisabled={!fullfillmentOption?.isOpen}
                                >
                                    <Caption
                                        text={'shop.add_to_cart'}
                                        color={button_text}
                                        mx={'auto'}
                                        fontSize={16}
                                    />
                                </HStack>
                            </HStack>
                        </Pressable>
                    </VStack>
                </ScrollView>
            </VStack>
        </Section>
        :
        <></>
    );
};
